import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, Outlet, useParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "app/store";
import { SpinModal } from "common/elements";
import { PiecePreview } from "features/piecePreview";
import { loadPiece } from "./slice";
import { MapMaker } from "./canvas";
import { PieceMilestone } from "./types";
import { recordEditHistory } from "./hook";

// URL の :pieceId からピースを API 取得してエディタ/プレビューへ渡すローダー。
// 取得が完了するまで子（Canvas/BuilderScene）をマウントしない。
// （先に空マップでシーンを構築すると loadPiece 後に loading=true のまま固まるため）
const BuilderPieceLoader = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pieceId } = useParams<{ pieceId: string }>();
  const uid = useSelector((state: RootState) => state.user.active?.uid);
  const loadedPieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!pieceId || !uid) return;
    // 同一 pieceId の再読み込み（編集↔プレビュー往復）は避ける。
    // 画面遷移のフェード（features/top の SwitchTransition）でこのローダーごと
    // 作り直されるため、判定はコンポーネント内の ref ではなくストアの状態で行う。
    // 再取得すると loadPiece.fulfilled が tested を false に戻し、
    // テスト済みの完成ステップが消えてしまう。
    if (loadedPieceId === pieceId) {
      setReady(true);
      return;
    }
    setReady(false);
    dispatch(loadPiece({ piece_id: pieceId, uid }))
      .unwrap()
      .then(({ own }) => {
        // 開いた区切りを記録に残す（他人の公開ピースを遊ぶときは残さない）
        if (own) dispatch(recordEditHistory(PieceMilestone.OPEN));
        setReady(true);
      })
      .catch(() => {
        // 存在しない/読めない piece_id で無理やり編集画面に来た → コースでまなぶトップへ戻す
        navigate("/courses", { replace: true });
      });
  }, [pieceId, uid, loadedPieceId, dispatch, navigate]);

  if (!ready) {
    return <SpinModal />;
  }

  return <Outlet />;
};

export const Builder = () => {
  return (
    <Routes>
      <Route path=":pieceId" element={<BuilderPieceLoader />}>
        <Route index element={<MapMaker />} />
        <Route path="preview" element={<PiecePreview />} />
      </Route>
    </Routes>
  );
};
